import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { RootReduxState } from 'app/shared/flux/reducers/index';
import type { ListingDetails } from 'app/types/listingDetails.type';
import UserItemActions from 'app/shared/flux/actions/UserItemActions';
import ImpressionTracking from 'app/shared/modules/ImpressionTracking';
import { ListingProvider } from 'app/shared/modules/listing-cards/ListingCardProvider';
import ListingCardV3 from 'app/shared/modules/listing-cards/ListingCardV3';
import {
  emitTrackClicksIntoPropertyEvent,
  emitTrackImpressionForPropertyCardEvent,
} from 'app/shared/models/Clickstream/ListingCardClickstreamEvents';
import Carousel from '../../../ui/Carousel';
import * as styles from './recentlyViewedModule.styles';

export const RECENTLY_VIEWED_LIMIT = 5;
export const PROPERTY_CARD_TRIGGER_LOCATION = 'recently_viewed_listings_module_halo' as const;
// Save/unsave events fired from within ListingCardV3's favorite button flow through these
// instead of the card-level trigger location, so keep the legacy values HALO-flavored.
const SAVE_HOME_TRIGGER_LOCATION = 'home_details_halo' as const;
const SAVE_HOME_TRIGGER_OBJECT = 'home_details_component|recently_viewed' as const;

type ViewedFilterableListing = Pick<ListingDetails, 'uriMalone' | 'uriBuilding' | 'uriV2'>;

export const selectRecentlyViewedListings = <T extends ViewedFilterableListing>(
  viewedListings: Array<T> | undefined,
  currentPathname: string,
): Array<T> => {
  if (!viewedListings) return [];
  return viewedListings.slice(0, RECENTLY_VIEWED_LIMIT).filter((listing) => {
    return (
      currentPathname !== listing.uriMalone &&
      currentPathname !== listing.uriBuilding &&
      currentPathname !== listing.uriV2
    );
  });
};

export const RecentlyViewedModule = () => {
  const dispatch = useDispatch();
  const viewedListings = useSelector(
    (state: RootReduxState) => state.listings?.listingGroups?.viewed as Array<ListingDetails> | undefined,
  );
  const currentPathname = useSelector((state: RootReduxState) => state.location?.current?.pathname ?? '');

  useEffect(() => {
    // @ts-expect-error -- UserItemActions is untyped (@ts-nocheck) but getUserItems returns a thunk
    dispatch(UserItemActions.getUserItems('viewed', 0, RECENTLY_VIEWED_LIMIT));
  }, [dispatch]);

  const filteredListings = useMemo(
    () => selectRecentlyViewedListings(viewedListings, currentPathname),
    [viewedListings, currentPathname],
  );

  const handleCardClick = useCallback((listing: ListingDetails) => {
    emitTrackClicksIntoPropertyEvent({
      listing,
      triggerLocation: PROPERTY_CARD_TRIGGER_LOCATION,
    });
  }, []);

  if (filteredListings.length === 0) return null;

  return (
    <div className={styles.container}>
      <Carousel gap="md" snapAlign="start" aria-label="Recently viewed homes">
        {filteredListings.map((listing) => (
          <Carousel.Item key={listing.maloneLotIdEncoded} width="300px">
            <ListingProvider
              listing={listing}
              triggerLocation={SAVE_HOME_TRIGGER_LOCATION}
              triggerObject={SAVE_HOME_TRIGGER_OBJECT}
            >
              <ListingCardV3
                isCarouselCard
                listingViewed={false}
                shouldLazyLoadListingCardPhotos
                forceUniformHeight
                onClick={() => handleCardClick(listing)}
              />
              <ImpressionTracking
                analyticsCallback={() =>
                  emitTrackImpressionForPropertyCardEvent({
                    listing,
                    triggerLocation: PROPERTY_CARD_TRIGGER_LOCATION,
                  })
                }
              />
            </ListingProvider>
          </Carousel.Item>
        ))}
      </Carousel>
    </div>
  );
};
